import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import ELK from 'elkjs/lib/elk.bundled.js';

const elk = new ELK();

// ---------------------------------------------------------------------------
// Sizing presets: compact (default) and expanded (on click)
// ---------------------------------------------------------------------------

const PRESETS = {
  compact: {
    nodeW: 90, nodeH: 32, productW: 60, productH: 22,
    fontSize: 7, productFontSize: 6.5, labelMaxLine: 13, productMaxLine: 10,
    spacing: '14', edgeSpacing: '32', layerSpacing: '32',
    maxCssWidth: 520, maxHeight: 140,
    showSpecies: false,
  },
  expanded: {
    nodeW: 140, nodeH: 44, productW: 90, productH: 30,
    fontSize: 10, productFontSize: 9, labelMaxLine: 18, productMaxLine: 14,
    spacing: '24', edgeSpacing: '56', layerSpacing: '56',
    maxCssWidth: 960, maxHeight: 320,
    showSpecies: true,
  },
  modal: {
    nodeW: 180, nodeH: 56, productW: 120, productH: 38,
    fontSize: 13, productFontSize: 11, labelMaxLine: 22, productMaxLine: 18,
    spacing: '32', edgeSpacing: '72', layerSpacing: '72',
    maxCssWidth: 9999, maxHeight: 9999,
    showSpecies: true,
  },
};

function fitFontSize(text, maxWidth, baseFontSize) {
  const charWidth = baseFontSize * 0.58;
  const textWidth = text.length * charWidth;
  if (textWidth <= maxWidth) return baseFontSize;
  return Math.max(baseFontSize * 0.55, baseFontSize * (maxWidth / textWidth));
}

function splitLabel(str, maxLineLen = 13) {
  if (!str) return [''];
  if (str.length <= maxLineLen) return [str];
  const words = str.split(/\s+/);
  if (words.length === 1) {
    return str.length > maxLineLen + 4 ? [str.slice(0, maxLineLen) + '…'] : [str];
  }
  const line1 = [];
  const line2 = [];
  let len = 0;
  for (const w of words) {
    if (len > 0 && len + w.length + 1 > maxLineLen && line2.length === 0) {
      line2.push(w);
      len = w.length;
    } else if (line2.length > 0) {
      line2.push(w);
    } else {
      line1.push(w);
      len += (len > 0 ? 1 : 0) + w.length;
    }
  }
  const l1 = line1.join(' ');
  let l2 = line2.join(' ');
  if (l2.length > maxLineLen + 2) l2 = l2.slice(0, maxLineLen) + '…';
  return l2 ? [l1, l2] : [l1];
}

function formatFlow(value, unit) {
  if (value == null) return null;
  const v = Math.abs(value) >= 1000
    ? value.toLocaleString(undefined, { maximumSignificantDigits: 3 })
    : parseFloat(value.toPrecision(3)).toString();
  const u = unit?.replace(/'/g, '') ?? '';
  return `${v} ${u}`.trim();
}

async function computeLayout(nodes, edges, preset) {
  const options = {
    'elk.algorithm': 'layered',
    'elk.direction': 'RIGHT',
    'elk.spacing.nodeNode': preset.spacing,
    'elk.layered.spacing.edgeNodeBetweenLayers': preset.edgeSpacing,
    'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
    'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
    'elk.edgeRouting': 'ORTHOGONAL',
    'elk.layered.spacing.nodeNodeBetweenLayers': preset.layerSpacing,
  };

  const graph = {
    id: 'root',
    layoutOptions: options,
    children: nodes.map(n => ({
      id: n.id,
      width: n.is_product ? preset.productW : preset.nodeW,
      height: n.is_product ? preset.productH : preset.nodeH,
    })),
    edges: edges.map((e, i) => ({
      id: `e${i}`,
      sources: [e.source],
      targets: [e.target],
    })),
  };

  const result = await elk.layout(graph);

  const positions = {};
  for (const child of result.children ?? []) {
    positions[child.id] = { x: child.x, y: child.y };
  }

  const edgeRoutes = {};
  for (const edge of result.edges ?? []) {
    const sections = edge.sections ?? [];
    if (sections.length > 0) edgeRoutes[edge.id] = sections[0];
  }

  return {
    positions,
    edgeRoutes,
    width: result.width ?? 300,
    height: result.height ?? 100,
  };
}

function buildPath(section) {
  if (!section) return '';
  const { startPoint, endPoint, bendPoints = [] } = section;
  const points = [startPoint, ...bendPoints, endPoint];
  if (points.length < 2) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L ${points[i].x} ${points[i].y}`;
  }
  return d;
}

function edgeMidpoint(section) {
  if (!section) return { x: 0, y: 0 };
  const { startPoint, endPoint, bendPoints = [] } = section;
  const points = [startPoint, ...bendPoints, endPoint];
  // Pick a point on the longest horizontal segment for cleaner label placement.
  let bestIdx = Math.floor(points.length / 2);
  let bestLen = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const dx = Math.abs(points[i + 1].x - points[i].x);
    if (dx > bestLen) { bestLen = dx; bestIdx = i; }
  }
  return {
    x: (points[bestIdx].x + points[bestIdx + 1].x) / 2,
    y: (points[bestIdx].y + points[bestIdx + 1].y) / 2,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function DiagramSvg({ nodes, edges, flows, layout, preset, expanded, arrowId, onClick, style, className }) {
  const { positions, edgeRoutes, width, height } = layout;
  const padX = 8;
  const padY = 8;
  const svgW = width + padX * 2;
  const svgH = height + padY * 2;

  return (
    <svg
      viewBox={`0 0 ${svgW} ${svgH}`}
      className={className}
      style={style}
      preserveAspectRatio="xMidYMid meet"
      onClick={onClick}
    >
      <defs>
        <marker
          id={arrowId}
          viewBox="0 0 5 3"
          refX="5"
          refY="1.5"
          markerWidth={expanded ? 6 : 5}
          markerHeight={expanded ? 4 : 3}
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 5 1.5 L 0 3 Z" fill="#a8a29e" />
        </marker>
      </defs>

      <g transform={`translate(${padX}, ${padY})`}>
        {edges.map((edge, i) => {
          const section = edgeRoutes[`e${i}`];
          const d = buildPath(section);
          if (!d) return null;

          const mid = preset.showSpecies ? edgeMidpoint(section) : null;
          const flowKey = `${edge.source}:${edge.target}`;
          const flowData = flows?.[flowKey];
          const flowLabel = flowData ? formatFlow(flowData.value, flowData.unit) : null;

          return (
            <g key={`e-${i}`}>
              <path
                d={d}
                fill="none"
                stroke={edge.emission ? '#e7e5e4' : '#d6d3d1'}
                strokeWidth={edge.emission ? 0.5 : (expanded ? 1 : 0.75)}
                strokeDasharray={edge.emission ? '2 2' : undefined}
                markerEnd={edge.emission ? undefined : `url(#${arrowId})`}
              />
              {preset.showSpecies && !edge.emission && edge.species && mid && (
                <text
                  x={mid.x}
                  y={mid.y - (flowLabel ? 5 : 2)}
                  textAnchor="middle"
                  fill="#a8a29e"
                  fontSize={7.5}
                  fontFamily="system-ui, -apple-system, sans-serif"
                >
                  {edge.species}
                </text>
              )}
              {preset.showSpecies && flowLabel && mid && !edge.emission && (
                <text
                  x={mid.x}
                  y={mid.y + 7}
                  textAnchor="middle"
                  fill="#78716c"
                  fontSize={7}
                  fontWeight={500}
                  fontFamily="system-ui, -apple-system, sans-serif"
                >
                  {flowLabel}
                </text>
              )}
            </g>
          );
        })}

        {nodes.map(node => {
          const pos = positions[node.id];
          if (!pos) return null;
          const nW = node.is_product ? preset.productW : preset.nodeW;
          const nH = node.is_product ? preset.productH : preset.nodeH;

          if (node.is_product) {
            const lines = splitLabel(node.label, preset.productMaxLine);
            const pad = nH * 0.4;
            const longestLine = lines.reduce((a, b) => a.length > b.length ? a : b, '');
            const fontSize = fitFontSize(longestLine, nW - pad, preset.productFontSize);
            const lineH = fontSize + 2;
            return (
              <g key={node.id} transform={`translate(${pos.x}, ${pos.y})`}>
                <rect
                  width={nW}
                  height={nH}
                  rx={nH / 2}
                  ry={nH / 2}
                  fill="#f5f5f4"
                  stroke="#a8a29e"
                  strokeWidth={0.75}
                  strokeDasharray="3 2"
                />
                {lines.map((line, li) => (
                  <text
                    key={li}
                    x={nW / 2}
                    y={lines.length > 1
                      ? nH / 2 - (lineH / 2) * (lines.length - 1) + li * lineH + 1
                      : nH / 2 + 1
                    }
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#78716c"
                    fontSize={fontSize}
                    fontStyle="italic"
                    fontFamily="system-ui, -apple-system, sans-serif"
                  >
                    {line}
                  </text>
                ))}
              </g>
            );
          }

          const lines = splitLabel(node.label, preset.labelMaxLine);
          const lineH = preset.fontSize + 2;

          return (
            <g key={node.id} transform={`translate(${pos.x}, ${pos.y})`}>
              <rect
                width={nW}
                height={nH}
                rx={expanded ? 4 : 3}
                ry={expanded ? 4 : 3}
                fill={node.is_anchor ? '#1e293b' : '#fafaf9'}
                stroke={node.is_anchor ? '#1e293b' : '#d6d3d1'}
                strokeWidth={0.75}
              />
              {lines.map((line, li) => (
                <text
                  key={li}
                  x={nW / 2}
                  y={lines.length > 1
                    ? nH / 2 - (lineH / 2) * (lines.length - 1) + li * lineH + 1
                    : nH / 2 + 1
                  }
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill={node.is_anchor ? '#f8fafc' : '#57534e'}
                  fontSize={preset.fontSize}
                  fontWeight={node.is_anchor ? 500 : 400}
                  fontFamily="system-ui, -apple-system, sans-serif"
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

DiagramSvg.propTypes = {
  nodes: PropTypes.array.isRequired,
  edges: PropTypes.array.isRequired,
  flows: PropTypes.object,
  layout: PropTypes.object.isRequired,
  preset: PropTypes.object.isRequired,
  expanded: PropTypes.bool,
  arrowId: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  style: PropTypes.object,
  className: PropTypes.string,
};

export default function PathwayDiagram({ topology }) {
  const [expanded, setExpanded] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [modal, setModal] = useState(false);
  const [layouts, setLayouts] = useState({});

  const { nodes = [], edges = [], flows } = topology ?? {};
  const preset = expanded ? PRESETS.expanded : PRESETS.compact;
  const presetKey = expanded ? 'expanded' : 'compact';

  useEffect(() => {
    if (nodes.length === 0) return;
    if (layouts[presetKey]) return;
    let cancelled = false;
    computeLayout(nodes, edges, preset).then(r => {
      if (!cancelled) setLayouts(prev => ({ ...prev, [presetKey]: r }));
    });
    return () => { cancelled = true; };
  }, [nodes, edges, presetKey, preset, layouts]);

  // Pre-compute the modal layout so it opens instantly.
  useEffect(() => {
    if (nodes.length === 0 || layouts.modal) return;
    let cancelled = false;
    computeLayout(nodes, edges, PRESETS.modal).then(r => {
      if (!cancelled) setLayouts(prev => ({ ...prev, modal: r }));
    });
    return () => { cancelled = true; };
  }, [nodes, edges, layouts.modal]);

  const toggleExpand = useCallback(() => setExpanded(e => !e), []);

  useEffect(() => {
    if (!modal) return;
    const onKey = e => { if (e.key === 'Escape') setModal(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [modal]);

  if (!topology || nodes.length === 0) return null;

  const layout = layouts[presetKey];
  const modalLayout = layouts.modal;

  const { width: layoutW = 300 } = layout ?? {};
  const maxCssWidth = Math.min((layoutW + 16) * 1.8, preset.maxCssWidth);

  return (
    <div className="mb-3">
      <button
        className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 select-none"
        onClick={() => setCollapsed(c => !c)}
      >
        <span className="text-[10px]">{collapsed ? '▸' : '▾'}</span>
        Supply chain
      </button>

      {!collapsed && layout && (
        <div className="relative group mt-1">
          <DiagramSvg
            nodes={nodes}
            edges={edges}
            flows={flows}
            layout={layout}
            preset={preset}
            expanded={expanded}
            arrowId={expanded ? 'topo-arrow-lg' : 'topo-arrow-sm'}
            onClick={toggleExpand}
            className="mx-auto cursor-pointer"
            style={{
              maxHeight: preset.maxHeight,
              width: '100%',
              maxWidth: maxCssWidth,
              display: 'block',
              transition: 'max-height 0.2s ease, max-width 0.2s ease',
            }}
          />
          {/* Fullscreen button — visible on hover */}
          <button
            className="absolute top-1 right-1 p-1 rounded bg-white/80 text-slate-400 hover:text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
            onClick={e => { e.stopPropagation(); setModal(true); }}
            title="View full size"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="10 2 14 2 14 6" />
              <polyline points="6 14 2 14 2 10" />
              <line x1="14" y1="2" x2="9.5" y2="6.5" />
              <line x1="2" y1="14" x2="6.5" y2="9.5" />
            </svg>
          </button>
        </div>
      )}

      {modal && modalLayout && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setModal(false)}
        >
          <div
            className="relative bg-white rounded-xl shadow-2xl flex items-center justify-center"
            style={{ width: '94vw', height: '88vh', padding: '2rem' }}
            onClick={e => e.stopPropagation()}
          >
            <button
              className="absolute top-3 right-4 text-slate-400 hover:text-slate-700 transition-colors text-lg leading-none z-10"
              onClick={() => setModal(false)}
            >
              ✕
            </button>
            <DiagramSvg
              nodes={nodes}
              edges={edges}
              flows={flows}
              layout={modalLayout}
              preset={PRESETS.modal}
              expanded
              arrowId="topo-arrow-modal"
              style={{ width: '100%', height: '100%', display: 'block' }}
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

PathwayDiagram.propTypes = {
  topology: PropTypes.shape({
    nodes: PropTypes.array,
    edges: PropTypes.array,
    flows: PropTypes.object,
  }),
};
